import React, { createElement, useEffect } from "react";
import "./App.css";
import LogoPNG from "./logo-circular.png";
//import { ESPLoader, connect } from 'esp-web-flasher';
import { ESPLoader, connect } from "adafruit-webserial-esptool";
import { CurrentVersionDef } from "./CurrentVersionDef";
import RICRunningFirmware from "./RICRunningFirmware";
import { RICSysInfoMsg } from "./RICTypes";
import RICUpdater from "./RICUpdater";
import { Line } from "rc-progress";
import RICRevision from "./RICRevision";
import VersionSelector from "./components/VersionSelector";
import {
  RICConnector,
  RICFileDownloadResult,
  RICHWElemList_Str,
  RICProgressCBType,
  RICSystemInfo,
} from "@robotical/ricjs";
import { RIC_WHOAMI_TYPE_CODE_ADDON_COLOUR } from "@robotical/ricjs-robotical-addons/dist/RICRoboticalAddOns";
import modalState from "./state-observables/modal/ModalState";
import ColourSensorCalibrationInstructionsModal from "./components/modals/ColourSensorCalibrationInstructionsModal";
import ColourSensorCalibrationProcess from "./components/modals/ColourSensorCalibrationProcess";

// Update path
const updateBasePath =
  "https://updates.robotical.io/[[FW_SOURCE]]/martyv2/rev[[REV_NUMBER]]/[[FW_VERSION]]";

// Handle updating of RIC
const GET_SYSTEM_INFO_FROM_RIC_SERIAL = false;

const ric: RICConnector = new RICConnector();

function logMsg(...args: any[]) {
  console.log(...args);
}

function debugMsg(...args: any[]) {
  console.debug(...args);
}

function errorMsg(...args: any[]) {
  console.error(...args);
}

function formatMacAddr(macAddr: number[]) {
  return macAddr
    .map((value) => value.toString(16).toUpperCase().padStart(2, "0"))
    .join(":");
}

async function getCurrentVersionJson(
  fwSource: string
): Promise<CurrentVersionDef> {
  const response = await fetch(
    updateBasePath.replace("[[FW_SOURCE]]", fwSource),
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );
  const json = await response.json();
  return json;
}

async function connectToESP(): Promise<ESPLoader | null> {
  let espLoader: ESPLoader | null = null;
  try {
    espLoader = await connect({
      log: (...args) => logMsg(...args),
      debug: (...args) => debugMsg(...args),
      error: (...args) => errorMsg(...args),
    });
  } catch (e) {
    console.error(`connectToESP: failed ${e}`);
    return null;
  }
  console.log(
    `connectToESP: espLoader = ${espLoader} isConn = ${espLoader.connected}`
  );

  // Check connected ok
  if (!espLoader || !espLoader.connected) {
    return null;
  }
  return espLoader;
}

async function initialiseESPLoader(
  espLoader: ESPLoader,
  timeoutCallback: () => void
): Promise<boolean> {
  // Initialise esp loader
  console.log(`connectToESP: initialising espLoader`);
  let espLoaderInitComplete = false;
  setTimeout(() => {
    if (!espLoaderInitComplete) {
      timeoutCallback();
      console.error(`connectToESP: espLoader init timeout`);
    }
  }, 10000);
  try {
    await espLoader.initialize();
  } catch (e) {
    await espLoader.disconnect();
    await espLoader.port.close();
    espLoaderInitComplete = true;
    console.error(`Failed to initialise ESPLoader: ${e}`);
    return false;
  }

  espLoaderInitComplete = true;
  console.log(`connectToESP: espLoader initialised`);

  logMsg("Connected to " + espLoader.chipName);
  logMsg("MAC Address: " + formatMacAddr(espLoader.macAddr()));

  return true;
}

async function startGettingSystemInfo(
  espLoader: ESPLoader,
  sysInfoCB: (sysInfo: RICSysInfoMsg) => void
) {
  // Hard reset
  await espLoader.hardReset();

  // Send v command
  await new Promise((resolve) => setTimeout(resolve, 2000));
  await espLoader.writeToStream(["v".charCodeAt(0), "\n".charCodeAt(0)]);

  // See if RIC Firmware is running and get version if so
  const ricRunningFirmware = new RICRunningFirmware(
    espLoader,
    espLoader.logger,
    sysInfoCB
  );

  // Start getting system info
  ricRunningFirmware.start();
}

function getStatusStr(
  loader: ESPLoader | null,
  flashSize: string | null,
  sysInfoJSON: string | null,
  ricVersion: string | null,
  ricHwRevision: number | null,
  progressMessage: string,
  progressPC: number,
  progressState: string
) {
  const progressClass =
    "status-item" +
    (progressState === "error"
      ? " status-warn"
      : progressPC === 100
        ? " status-complete"
        : "");
  return (
    <div className="status-line">
      {/* {loader ? <div className='status-item'>Chip Name: {loader.chipName}</div> : <div></div>} */}
      {/* {flashSize ? <div className='status-item'>Flash Size: {flashSize}</div> : <div></div>} */}
      {ricVersion ? (
        <div className="status-item">RIC Version: {ricVersion}</div>
      ) : (
        <div></div>
      )}
      {ricHwRevision ? (
        <div className="status-item">
          RIC HW Revision: {ricHwRevision} (Flash Size {flashSize})
        </div>
      ) : (
        <div></div>
      )}
      {sysInfoJSON ? (
        <div className="status-item">System Info: {sysInfoJSON}</div>
      ) : (
        <div></div>
      )}
      {progressMessage ? (
        <div className={progressClass}>
          <div className="status-label">Progress: {progressMessage}</div>
          <div>
            <Line percent={progressPC} strokeWidth={3} strokeColor="#ffda61" />
          </div>
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
}

function LandingPage() {
  const [loader, setLoader] = React.useState<ESPLoader | null>(null);
  const [sysInfoJSON, setSysInfoJSON] = React.useState<string | null>(null);
  const [ricVersion, setRICVersion] = React.useState<string | null>(null);
  const [ricHwRevision, setRICHwRevision] = React.useState<number | null>(null);
  const [retryCount, setRetryCount] = React.useState<number>(0);
  const [progressMessage, setProgressMessage] = React.useState<string>("");
  const [progressPC, setProgressPC] = React.useState<number>(0);
  const [progressState, setProgressState] = React.useState<string>("ok");
  const [flashSize, setFlashSize] = React.useState<string | null>(null);
  const [fwSource, setFwSource] = React.useState<string>("live");
  const [isDev, setIsDev] = React.useState<boolean>(false);
  const [devFwVersion, setDevFwVersion] = React.useState<string>(
    "current_version.json"
  );
  const [error, setError] = React.useState<string | null>(null);
  const [versions, setVersions] = React.useState<string[]>([]);
  const [eraseFirst, setErasefirst] = React.useState<boolean>(false);

  const firmwareBaseUrl = "https://updates.robotical.io";

  useEffect(() => {
    if (!isWebSerialSupported()) {
      setError("Your browser is not compatible with the Marty Updater");
      alert("Your browser is not compatible with the Marty Updater");
    }
    const asyncFunc = async () => {
      const fwVersions = await getDevVersions();
      if (!fwVersions) return;
      setVersions(fwVersions || []);
      //setDevFwVersion(fwVersions![0] || "current_version.json");
    };
    asyncFunc();
  }, []);

  function onProgress(state: string, progressPC: number, isOk: boolean) {
    logMsg(`${state}: ${progressPC}%`);
    if (progressPC !== 0) {
      setProgressMessage(state + " " + progressPC.toFixed(0) + "%");
    } else {
      setProgressMessage(state);
    }
    setProgressPC(progressPC);
    setProgressState(isOk ? "ok" : "error");
  }

  async function getDevVersions() {
    const fetchResponse = await fetch(
      `${firmwareBaseUrl}/testing/martyv2/versions.json`
    );
    if (!fetchResponse || !fetchResponse.ok) return [];
    const versionsJson = await fetchResponse.json();
    let versions = versionsJson["versions"];
    versions.unshift("current_version.json");
    return versions;
  }

  function isWebSerialSupported() {
    return "serial" in navigator;
  }

  function espLoaderTimeout() {
    console.error("espLoaderTimeout");
    setLoader(null);
    setProgressMessage(
      "Cannot connect: please refresh the page and try again ensuring the port is correct and nothing else is using the connection"
    );
    setProgressPC(0);
    setProgressState("error");
  }

  useEffect(() => {
    checkForDevFw();
  });

  function checkForDevFw() {
    const params = window.location.search;
    console.log("params: " + params);
    if (params.includes("dev")) setIsDev(true);
  }

  async function checkIsColourSensCalibrationRequired() {
    console.log("\n==== Checking if colour sensor calibration is required ====");
    // check if the connected RIC has at least one coloursensor on version 1.3.0, with DTID of 0x0097
    // if so, we need to calibrate the colour sensor after updating
    const ricHWListStr = await ric.getRICMsgHandler()
      .sendRICRESTURL<RICHWElemList_Str>(`hwstatus/strstat/?filterByType=${RIC_WHOAMI_TYPE_CODE_ADDON_COLOUR}`);
    const hwElems = ricHWListStr.hw;
    if (hwElems && hwElems.length) {
      if (typeof hwElems[0] !== "object") {
        // we are on a fw version that doesn't support strstat, so we can assume that they don't have a batch 5 coloursensor
        return false;
      } else {
        const hwElemList = RICHWElemList_Str.expand(ricHWListStr);
        console.log("hwElemList: ", hwElemList);
        const colourSensElems = hwElemList.hw.filter(
          (elem) => elem.whoAmI === RIC_WHOAMI_TYPE_CODE_ADDON_COLOUR
        );
        console.log("colourSensElems: ", colourSensElems);
        // check if any of the colour sensors are on version 1.3.0
        const isAnyColourSensOn130 = colourSensElems && (!!colourSensElems.length) && colourSensElems.some((elem) => elem.versionStr === "1.3.0");
        console.log("isAnyColourSensOn130: ", isAnyColourSensOn130);
        // check if any of the colour sensors have a DTID of 0x0097
        // const isAnyColourSensOn0097 = colourSensElems && !!colourSensElems.length && colourSensElems.some((elem) => elem.whoAmITypeCode === "97");
        // console.log("isAnyColourSensOn0097: ", isAnyColourSensOn0097);
        return isAnyColourSensOn130;
      }
    }
    return false;
  }

  function selectVersionHandler(version: string) {
    setDevFwVersion(version);
  }

  async function downloadFile(
    downloadUrl: string,
    progressCB: RICProgressCBType = (received, total) => { }
  ): Promise<RICFileDownloadResult> {
    const fileDownloadResult = new RICFileDownloadResult();
    console.log(`Fetching ${downloadUrl}`);
    const fetchResponse = await fetch(downloadUrl);
    if (!fetchResponse.ok) {
      console.log(`Failed to get firmware file ${downloadUrl}`);
      return fileDownloadResult;
    }
    const fileBlob = await fetchResponse.blob();
    fileDownloadResult.fileData = new Uint8Array(await fileBlob.arrayBuffer());
    fileDownloadResult.downloadedOk = true;
    return fileDownloadResult;
  }

  function getUpdatePath(revNo: number | null = ricHwRevision) {
    console.log(
      `fwSource: ${fwSource} devFwVersion ${devFwVersion} revNo ${ricHwRevision}`
    );
    let updatePath = updateBasePath.replace("[[FW_SOURCE]]", fwSource);

    if (devFwVersion === "current_version.json" || devFwVersion === "") {
      updatePath = updatePath.replace("[[FW_VERSION]]", "current_version.json");
    } else {
      updatePath = updatePath.replace(
        "[[FW_VERSION]]",
        devFwVersion + "/" + devFwVersion + ".json"
      );
    }

    if (revNo) {
      updatePath = updatePath.replace("[[REV_NUMBER]]", revNo.toString());
    }

    return updatePath;
  }

  async function getVersionFromPath(path: string) {
    try {
      const fetchResponse = await fetch(path);
      if (!fetchResponse || !fetchResponse.ok) return "";
      const versionFileJson = await fetchResponse.json();
      return versionFileJson["firmwareVersion"];
    } catch (err) {
      console.log(`Error getting expected version from file ${path} - ${err}`);
      return "";
    }
  }

  async function updateESP(useRICport = true, expectedVersion = "") {
    let espLoader: ESPLoader | null = null;
    if (useRICport) {
      ric.pauseConnection(true);
      // use existing port from ricjs connection
      espLoader = new ESPLoader(ric.getConnLocator(), {
        log: (...args) => logMsg(...args),
        debug: (...args) => debugMsg(...args),
        error: (...args) => errorMsg(...args),
      });
    } else {
      espLoader = await connectToESP();
    }

    if (!espLoader) return false;

    setProgressMessage("Connecting, please wait...");
    if (!(await initialiseESPLoader(espLoader, espLoaderTimeout))) return false;

    // Set the loader
    console.log("Setting loader");
    setLoader(espLoader);
    console.log("Setting loader done");

    // Hardware revision
    const ricRevision = new RICRevision();

    console.log(`Getting system info`);

    // Start getting System Info
    // TODO: consolidate this functionality
    if (GET_SYSTEM_INFO_FROM_RIC_SERIAL) {
      await startGettingSystemInfo(espLoader, (sysInfo: RICSysInfoMsg) => {
        console.log("System Info: ", sysInfo);
        setSysInfoJSON(JSON.stringify(sysInfo));
        if (sysInfo.v) {
          setRICVersion(sysInfo.v);
        }
        if (sysInfo.r) {
          setRICHwRevision(sysInfo.r);
        }
      });

      const currVersion = await getCurrentVersionJson(fwSource);
      console.log(currVersion);
    }

    // Run the ESP loader stub
    const espStub = await espLoader.runStub();
    if (!espStub) return false;

    // Get flash size
    const flashSize = espStub.flashSize;

    // Compute RIC revision
    if (flashSize) {
      ricRevision.set(flashSize ?? "");
      setRICHwRevision(ricRevision.hwRevision);
      setFlashSize(flashSize);
    }

    const updatePath = getUpdatePath(ricRevision.hwRevision);

    console.log("updatePath: " + updatePath);
    // Start updating RIC
    const ricUpdater = new RICUpdater(
      espLoader,
      espStub,
      eraseFirst,
      onProgress
    );

    await ricUpdater.update(updatePath);

    if (useRICport) {
      // wait for RIC to finish starting up
      await new Promise((resolve) => setTimeout(resolve, 10000));
      // Disconnect
      await espLoader.disconnect();
      // baud rate will have been changed during update. close port and reopen with expected baud
      await espLoader.port.close();

      try {
        await ric.getConnLocator().open({ baudRate: 115200 });
        ric.pauseConnection(false);
        if (!ric.getRICChannel()) return false;

        // double check connection is still open and read loop is running
        console.log(
          `RIC Channel connected: ${ric.getRICChannel()!.isConnected()}`
        );
        if (!ric.getRICChannel()!.isConnected()) {
          await ric.getRICChannel()!.connect("reusePort");
        }

        // check actual firmware version matches expected, and that webserial connection is functioning
        const sysInfo = await ric
          .getRICMsgHandler()
          .sendRICRESTURL<RICSystemInfo>("v");
        console.log(`Detected new system version ${sysInfo.SystemVersion}`);
        if (expectedVersion != "" && sysInfo.SystemVersion != expectedVersion) {
          console.error(
            `Detected version (${sysInfo.SystemVersion}) does not match expected (${expectedVersion})`
          );
          return false;
        }
      } catch (err) {
        console.error(`Error communicating with ric: ${err}`);
        return false;
      }
    } else {
      await espLoader.disconnect();
      await espLoader.port.close();
    }

    return true;
  }

  const startUpdateHandler = async () => {
    // Clear state
    setProgressPC(0);
    setProgressMessage("");
    setProgressState("ok");
    setLoader(null);
    setRICVersion(null);
    setRetryCount(0);
    setRICHwRevision(null);

    if (!(await ric.connect("WebSerial", ""))) return;
    console.log(`RIC conn locator ` + ric.getConnLocator());
    ric.setupUpdateManager(
      "",
      "",
      `${firmwareBaseUrl}/${fwSource}`,
      downloadFile
    );

    const sysInfo = await ric.getRICSystem().getRICSystemInfo();
    let elemsToUpdate: Array<any> | null = null;
    let expectedVersion = "";

    // if there was a failed previous update, we won't be able to get system version or query hwelems
    // the main ESP update will still function and will rescue the board
    let isColourSensCalibrationRequired = false;
    if (sysInfo && sysInfo["SystemVersion"] != "0.0.0") {
      console.log("sysinfo: " + JSON.stringify(sysInfo));
      const systemVersion = sysInfo["SystemVersion"];

      isColourSensCalibrationRequired =
        await checkIsColourSensCalibrationRequired();
      if (isColourSensCalibrationRequired) {
        await modalState.setModal(
          createElement(ColourSensorCalibrationInstructionsModal),
          "Colour Sensor Calibration Required"
        );
      }

      const revNo = sysInfo.RicHwRevNo;
      console.log(`Detected RIC fw v${systemVersion} revision ${revNo}`);
      if (revNo) setRICHwRevision(revNo);

      expectedVersion = await getVersionFromPath(getUpdatePath(revNo));
      console.log(`Updating to version ${expectedVersion}`);

      // ensure 5v power is on, in case robot has been idle for a while
      await ric.getRICMsgHandler().sendRICRESTURL("pwrctrl/5von");
      await new Promise((resolve) => setTimeout(resolve, 1000));

      elemsToUpdate = await ric.getRICUpdateManager()!.elemUpdatesRequired();
    }

    // TODO:
    // check if any updates are required
    // alert user if no updates required

    // Handle failure
    if (!(await updateESP(true, expectedVersion))) {
      setLoader(null);
      setRetryCount(1);
    }

    onProgress("Updating servos and addons...", 80, true);

    await ric.getRICMsgHandler().sendRICRESTURL("hwstatus");

    // check again elemsToUpdate and isColourSensCalibrationRequired, in case original fw was corrupted and we didn't get this before
    elemsToUpdate = await ric.getRICUpdateManager()!.elemUpdatesRequired();
    isColourSensCalibrationRequired ||= await checkIsColourSensCalibrationRequired();

    if (!(await ric.getRICUpdateManager()!.updateElems(elemsToUpdate))) {
      setLoader(null);
      setRetryCount(1);
    }

    // starting colour sensor calibration process if required
    if (isColourSensCalibrationRequired) {
      console.log("Colour sensor calibration required");
      await modalState.setModal(
        createElement(ColourSensorCalibrationProcess, { ricConnector: ric }), "Colour Sensor Calibration Required");
    }

    onProgress("Firmware update successful", 100, true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    ric.disconnect();
  };

  return (
    <div className="App">
      <div className="header-block">
        <div className="header-group">
          <img
            src={LogoPNG}
            alt="Robotical Logo"
            className="robotical-logo"
          ></img>
          <div
            id="pageHeadingId"
            className="content-head-item content-master-head"
          >
            Marty Update
          </div>
        </div>
      </div>
      <div className="content-block">
        <ol className="content-list">
          <li className="content-item">
            Connect Marty to your computer using a USB cable
          </li>
          <li className="content-item">
            Click the button below to update Marty
          </li>
        </ol>
        <div className="content-info">
          <p>
            After you click you will see a pop-up saying this program "wants to
            connect to a serial port."
          </p>
          <p>
            If one of the options says "CP2102N" or "USB Serial" then please
            select that one.
          </p>
          <p>After making a selection please press Connect.</p>
        </div>
      </div>

      <div
        className="content-block"
        id="devFirmare"
        style={{ display: isDev ? "inline" : "none" }}
      >
        Firmware source:{" "}
        <select
          value={fwSource}
          onChange={(event) => setFwSource(event.target.value)}
        >
          <option value="live">Live</option>
          <option value="testing">Testing</option>
        </select>
      </div>

      {fwSource === "testing" && (
        <div className="content-block">
          <VersionSelector
            versions={versions}
            onChange={selectVersionHandler}
          />
        </div>
      )}

      {fwSource === "testing" && (
        <div className="content-block">
          <label htmlFor="eraseFirst">Erase first?</label>
          <input
            type="checkbox"
            id="eraseFirst"
            name="eraseFirst"
            value="eraseFirst"
            checked={eraseFirst}
            onChange={(event) => setErasefirst(event.target.checked)}
          />
        </div>
      )}

      <div className="content-block">
        <div className="content-options">
          <button className="button-large" onClick={startUpdateHandler}>
            Start Update
          </button>
          <div className="content-status">
            {error && <div className="status-item status-warn">{error}</div>}
          </div>
          <div className="content-status">
            {retryCount > 0 ? (
              <div className="status-item status-warn">
                Please refresh the page, check cables, check nothing else using
                serial port, and retry
              </div>
            ) : (
              <div className="status-box">
                {getStatusStr(
                  loader,
                  flashSize,
                  sysInfoJSON,
                  ricVersion,
                  ricHwRevision,
                  progressMessage,
                  progressPC,
                  progressState
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LandingPage;
